<script setup lang="ts">
/**
 * One control, drawn as a key cap.
 *
 * Extracted from `GameHud`'s tutorial overlay so the loading screen can show the
 * same caps without drawing a second set. That matters more than the saved lines:
 * two independent drawings of one control is exactly the shape of thing this
 * codebase keeps getting bitten by — they agree on the day they are written and
 * drift on some later one, and a player who learns a shape on the loading screen
 * and meets a different shape in the HUD has learned nothing.
 *
 * **Drawn, not fetched**, for the reason the radar bezel, the title badges and the
 * currency marks are: this is a rounded rectangle with a letter in it at ~14
 * device pixels, and any fixed raster of that is soft on half the machines that
 * run the game.
 */
import { computed } from 'vue';

import type { TutorialKeyKind } from '@/game/tutorial';

const props = withDefaults(
  defineProps<{
    /** The letter or word on the cap. Empty for the mouse glyphs, which are drawn. */
    cap: string;
    kind: TutorialKeyKind;
    /** A word rather than a letter, so the cap is not forced square. */
    wide?: boolean;
    /**
     * The "press this now" signal.
     *
     * Off by default, because the loading screen is showing controls rather than
     * asking for one — a screen where everything pulses is a screen that says
     * nothing. The HUD passes it per goal.
     */
    pulse?: boolean;
  }>(),
  { wide: false, pulse: false },
);

/*
 * Which of the three drawings this cap wears, if any.
 *
 * Split into two predicates rather than one `kind !== 'key'`, because a cap now
 * has three shapes and a letter: a `kind` this component has never heard of has
 * to fall through to the letter rather than to whichever branch happens to be
 * last. That is not hypothetical — the touch kinds *were* the new kinds, and
 * before this the mouse branch caught them and drew a mouse for a stick.
 */
const mouseKind = computed(() => props.kind.startsWith('mouse-'));
const touchKind = computed(() => props.kind.startsWith('touch-'));
</script>

<template>
  <kbd class="keycap" :class="{ wide, mouse: kind !== 'key', pulse }" :data-key="cap">
    <svg v-if="mouseKind" class="keycap-mouse" viewBox="0 0 12 18" aria-hidden="true">
      <rect x="0.75" y="0.75" width="10.5" height="16.5" rx="5.25" />
      <path v-if="kind === 'mouse-left'" class="fill" d="M0.75 6V6a5.25 5.25 0 0 1 5.25-5.25V6Z" />
      <path v-if="kind === 'mouse-right'" class="fill" d="M11.25 6V6a5.25 5.25 0 0 0-5.25-5.25V6Z" />
      <path v-if="kind === 'mouse-move'" class="stroke" d="M6 1v5M2 9h8" />
    </svg>

    <svg v-else-if="touchKind" class="keycap-touch" viewBox="0 0 16 16" aria-hidden="true">
      <template v-if="kind === 'touch-stick'">
        <circle cx="8" cy="8" r="6.6" />
        <circle class="fill" cx="8" cy="5.4" r="2.6" />
      </template>
      <template v-else-if="kind === 'touch-fire'">
        <circle cx="8" cy="8" r="6.9" />
        <path class="fill" d="M8 3.2c1.5 1.2 2.3 2.7 2.3 4.4v0.6H5.7V7.6C5.7 5.9 6.5 4.4 8 3.2Z" />
        <rect x="5.9" y="9.1" width="4.2" height="4.4" rx="0.7" />
      </template>
      <template v-else>
        <circle class="fill" cx="8" cy="8" r="2.2" />
        <path class="stroke" d="M2.6 8h2.4M11 8h2.4" />
        <path class="stroke" d="M3.9 6.7 2.4 8l1.5 1.3M12.1 6.7 13.6 8l-1.5 1.3" />
      </template>
    </svg>

    <span class="keycap-text">{{ cap }}</span>
  </kbd>
</template>

<style scoped>
.keycap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.35rem;
  border: 1px solid var(--accent, #e8722c);
  /* The lip. A cap reads as a *key* because it has a bottom edge the top face
     does not, which is one border and one inset shadow rather than an image. */
  border-radius: 0.25rem;
  box-shadow:
    inset 0 -2px 0 rgba(0, 0, 0, 0.45),
    0 1px 0 rgba(0, 0, 0, 0.5);
  background: rgba(232, 114, 44, 0.16);
  color: var(--accent, #e8722c);
  font-family: ui-monospace, 'SF Mono', monospace;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
}

/* A word, not a letter — so it is not forced square. */
.keycap.wide {
  font-family: inherit;
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.keycap-mouse {
  width: 0.62rem;
  height: 0.93rem;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.2;
}

/*
 * Square rather than the mouse's 12x18, because none of the three is a tall
 * object: a stick ring, a fire circle and a fingertip are all as wide as they
 * are high, and forcing them into the mouse's box would draw ellipses.
 */
.keycap-touch {
  width: 0.85rem;
  height: 0.85rem;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.3;
}

.keycap-mouse .fill,
.keycap-touch .fill {
  fill: currentColor;
  stroke: none;
}

.keycap-mouse .stroke,
.keycap-touch .stroke {
  stroke-linecap: round;
  stroke-linejoin: round;
}

/*
 * The pulse is the "press this now" signal, and it is on **opacity and shadow
 * only** — never on transform or size.
 *
 * A cap that grew and shrank would reflow the row under it thirty times a second,
 * on a panel that hangs over the middle of the screen. It also stops dead once the
 * goal is done, so a finished drill is silent rather than still asking.
 */
@keyframes keycap-pulse {
  0%,
  100% {
    box-shadow:
      inset 0 -2px 0 rgba(0, 0, 0, 0.45),
      0 1px 0 rgba(0, 0, 0, 0.5),
      0 0 0 0 rgba(232, 114, 44, 0);
  }
  50% {
    box-shadow:
      inset 0 -2px 0 rgba(0, 0, 0, 0.45),
      0 1px 0 rgba(0, 0, 0, 0.5),
      0 0 0.5rem 0 rgba(232, 114, 44, 0.55);
  }
}

.keycap.pulse {
  animation: keycap-pulse 1.6s ease-in-out infinite;
}

/* Reduced motion drops the movement, never the information: the cap stays lit,
   it just stops breathing. */
@media (prefers-reduced-motion: reduce) {
  .keycap.pulse {
    animation: none;
    box-shadow:
      inset 0 -2px 0 rgba(0, 0, 0, 0.45),
      0 1px 0 rgba(0, 0, 0, 0.5),
      0 0 0.4rem 0 rgba(232, 114, 44, 0.45);
  }
}
</style>
